import { Cite, plugins, util } from "@citation-js/core";
import "@citation-js/plugin-doi";
import "@citation-js/plugin-csl";
import "@citation-js/plugin-bibtex";
import "@citation-js/plugin-ris";
import "@citation-js/plugin-pubmed";
import { ensureLocaleRegistered } from "@/lib/csl-locale-registry";
import { normalizeOutputLocale } from "@/lib/output-locales";

const BUILTIN_STYLES = ["apa", "vancouver", "harvard1"] as const;
const CSL_STYLES_REPO =
  "https://raw.githubusercontent.com/citation-style-language/styles/master";

// Top-level regex patterns for performance
const VALID_STYLE_SLUG_REGEX = /^[a-z0-9-]+$/;
const DOI_PREFIX_REGEX = /^10\.\d/;
const DOI_PATHNAME_REGEX = /^\/10\.\d/;
const ISBN_10_REGEX = /^\d{9}[\dX]$/i;
const ISBN_13_REGEX = /^\d{13}$/;
// PubMed identifiers. PMIDs are bare numerics (≤8 digits today, ~38M records); a bare
// digit string can't be an ISBN (needs 10/13 digits) or a DOI (needs `10.`), so routing
// it to the PubMed resolver after the ISBN/URL/DOI checks is unambiguous.
const PMID_BARE_REGEX = /^\d{1,8}$/;
const PMID_PREFIX_REGEX = /^pmid:\s*(\d{1,8})$/i;
const PMCID_REGEX = /^PMC\d+$/i;
const PUBMED_URL_HOST = "pubmed.ncbi.nlm.nih.gov";
const PUBMED_URL_PATH_REGEX = /^\/(\d{1,8})\/?$/;
// arXiv identifiers (current scheme since Apr 2007: `YYMM.NNNNN` + optional `vN`). Every
// arXiv paper has a deterministic DataCite DOI — `10.48550/arXiv.<id>` (version stripped) —
// so we resolve through the existing DOI path with no new fetch surface. The mandatory dot
// keeps the bare form from colliding with the bare-PMID branch (pure digits, no dot).
const ARXIV_PREFIX_REGEX = /^arxiv:\s*(\d{4}\.\d{4,5})(?:v\d+)?$/i;
const ARXIV_BARE_REGEX = /^(\d{4}\.\d{4,5})(?:v\d+)?$/;
const ARXIV_URL_HOST = "arxiv.org";
const ARXIV_URL_PATH_REGEX =
  /^\/(?:abs|pdf)\/(\d{4}\.\d{4,5})(?:v\d+)?(?:\.pdf)?\/?$/i;
// YouTube video URLs. A pasted YouTube link is resolved through the keyless oEmbed endpoint
// (issue #96) BEFORE the generic HTML scraper, so it produces a correct [Video] citation with
// the channel as author instead of a plain `webpage`. A YouTube video id is exactly 11 chars
// from the URL-safe base64 alphabet; the strict length keeps detection from hijacking other
// youtube.com paths (channels, playlists, /results) that have no single video to cite.
const YOUTUBE_ID_REGEX = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_PATH_REGEX = /^\/(?:shorts|live|embed|v)\/([A-Za-z0-9_-]{11})/;
const YOUTUBE_HOST = "youtube.com";
const YOUTUBE_SHORT_HOST = "youtu.be";
const YOUTUBE_OEMBED_ENDPOINT = "https://www.youtube.com/oembed";
const YEAR_REGEX = /\d{4}/;
const HYPHEN_REGEX = /-/g;
// RIS records open with a `TY  - <type>` tag line. Used only to label errors and
// analytics for the bulk-import dispatch — Citation.js auto-detects the actual format.
const RIS_FORMAT_REGEX = /^TY\s{2}-\s/m;
const BIBLIOGRAPHY_ERROR_REGEX = /not iterable|bibliography/i;
const ISO_DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})/;
const YEAR_ONLY_REGEX = /^(\d{4})/;
const ISO_DATE_FULL_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
const YEAR_MONTH_REGEX = /^(\d{4})-(\d{2})$/;
const HTML_TAGS_REGEX = /<[^>]+>/g;
const WHITESPACE_REGEX = /\s+/g;
const META_ESCAPE_REGEX = /[.*+?^${}()|[\]\\]/g;
// Scholarly-metadata extraction (issue #7): parse every <meta> tag once, then read the
// Highwire (citation_*) / Dublin Core (DC.*) / JSON-LD families. All module-level per the
// project's "no regex inside loops" rule.
const META_TAG_REGEX = /<meta\b[^>]*>/gi;
const META_NAME_ATTR_REGEX = /\b(?:name|property)\s*=\s*["']([^"']*)["']/i;
const META_CONTENT_ATTR_REGEX = /\bcontent\s*=\s*["']([^"']*)["']/i;
const JSON_LD_SCRIPT_REGEX =
  /<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
// Matches Article, NewsArticle, ScholarlyArticle, BlogPosting, etc.
const ARTICLE_TYPE_REGEX = /article|posting/i;
const SLASH_DATE_REGEX = /^(\d{4})\/(\d{1,2})\/(\d{1,2})/;
const SLASH_YEAR_MONTH_REGEX = /^(\d{4})\/(\d{1,2})$/;
// Splits an author string on explicit, unambiguous multi-author separators:
// semicolons, ampersands, and the word "and" (consuming a preceding Oxford comma).
const AUTHOR_LIST_SEPARATOR_REGEX = /\s*(?:;|,?\s*&|,?\s+and\s+)\s*/i;
const NAME_WHITESPACE_REGEX = /\s+/;
// An initial in a given-name part ("J.", "J", "J.-P.", "H.C."), and a capitalized given
// name beside it ("James D."), for telling an inverted list from a list of direct names.
const INITIAL_TOKEN_REGEX = /^(?:\p{Lu}\.?)(?:-?\p{Lu}\.?)*$/u;
const CAPITALIZED_WORD_REGEX = /^\p{Lu}[\p{Ll}'’-]+$/u;
// A name wrapped in braces, "{R Core Team}", is a group or organization author kept
// verbatim as a CSL `literal` — the convention BibTeX uses for corporate authors. The
// braces also shield commas, "&" and "and" inside the name from the list splitter, which
// masks each braced run with a placeholder before splitting and restores it after.
const BRACED_NAME_REGEX = /\{[^{}]*\}/g;
const BRACED_SINGLE_NAME_REGEX = /^\{([^{}]*)\}$/;
const BRACED_PLACEHOLDER_REGEX = /\uE000(\d+)\uE000/g;
const BRACED_PLACEHOLDER_ONLY_REGEX = /^\uE000\d+\uE000$/;
// Splits a name fragment into tokens on whitespace OR commas, so a trailing CSL suffix
// can be lifted out of either "Martin Luther Jr." or "Martin Luther, Jr.".
const NAME_PART_SPLIT_REGEX = /[\s,]+/;
// Closed allow-list of generational suffixes (CSL `suffix`). Conservative on purpose:
// only these unambiguous tokens are treated as a suffix; everything else is a name part.
const NAME_SUFFIX_REGEX = /^(?:jr|sr|ii|iii|iv|v)\.?$/i;
// Surname particles folded into CSL `non-dropping-particle` (kept with the surname for
// inversion/sorting in APA/Chicago/MLA). Matched case-SENSITIVELY against lowercase only,
// so a capitalized real surname ("Van", Vietnamese "Le") is never mistaken for a particle.
const NAME_PARTICLES = new Set([
  "van",
  "von",
  "de",
  "del",
  "della",
  "der",
  "da",
  "di",
  "du",
  "la",
  "le",
  "den",
  "ter",
  "ten",
  "bin",
  "al",
]);
// A particle ending in an apostrophe or hyphen ("d'", "al-") is set against the surname,
// as citeproc prints it, so the re-parsed name keeps it on the surname side.
const ATTACHED_PARTICLE_REGEX = /['’-]$/;
// Heuristic guard so multi-word organizational names stay verbatim ({ literal })
// instead of being wrongly inverted/initialized as a person. Not exhaustive — covers
// the common cases (see makePerson docstring); refining it is a follow-up.
const ORGANIZATION_NAME_REGEX =
  /\b(?:team|collaboration|inc|ltd|llc|corp|corporation|company|co|association|organization|organisation|university|college|school|institute|department|press|committee|society|foundation|group|office|bureau|council|commission|agency|ministry|center|centre|government|national|international|federal|board|division|laboratory|labs?|service|services|administration|authority|trust|union|league|alliance|network|consortium|partnership|partners)\b/i;

/** Valid CSL style slug (filename without .csl; may include hyphen e.g. dependent-2d-materials). */
export function isValidStyleSlug(slug: string): boolean {
  return (
    VALID_STYLE_SLUG_REGEX.test(slug) && slug.length >= 2 && slug.length <= 120
  );
}

const styleCache = new Set<string>();

/** Fetch path for GitHub (root = slug, dependent = e.g. "dependent/2d-materials"). */
function getFetchPath(slug: string, cslPath?: string): string {
  return cslPath ?? slug;
}

/** Fetch CSL XML from GitHub and register with Citation.js. */
// English short-form terms the MLA family relies on that citeproc's bundled en-US locale
// predates. MLA 9 abbreviates months except May, June and July and writes "Sept."
// (style.mla.org works-cited examples: "26 Sept. 2019", "30 June 2020", "14 July 2020");
// the current CSL en-US locale (locales-en-US.xml) defines the same short forms and the
// "review of" term the MLA style uses for reviews. The bundled locale still has "Jun.",
// "Jul.", "Sep." and no "review-of", so these ride in the style's own en locale.
const MLA_LOCALE_TERMS: { name: string; form?: string; value: string }[] = [
  { name: "month-06", form: "short", value: "June" },
  { name: "month-07", form: "short", value: "July" },
  { name: "month-09", form: "short", value: "Sept." },
  { name: "review-of", value: "review of" },
  { name: "review-of", form: "short", value: "rev. of" },
];
const MLA_STYLE_SLUG_REGEX = /^modern-language-association(?:-|$)/;
const STYLE_EN_LOCALE_TERMS_REGEX =
  /(<locale\s+xml:lang="en(?:-US)?"\s*>[\s\S]*?<terms>)/;
const STYLE_INFO_END = "</info>";

function cslTermDefined(xml: string, name: string, form?: string): boolean {
  return new RegExp(
    `<term\\b(?=[^>]*\\bname="${name}")${
      form ? `(?=[^>]*\\bform="${form}")` : "(?![^>]*\\bform=)"
    }`
  ).test(xml);
}

/**
 * Patch a fetched style's XML before registering it. Only the MLA family is touched:
 * it gains the en short-month and "review of" terms above unless it defines them itself.
 */
function adaptStyleXml(slug: string, xml: string): string {
  if (!MLA_STYLE_SLUG_REGEX.test(slug)) {
    return xml;
  }
  const terms = MLA_LOCALE_TERMS.filter(
    (term) => !cslTermDefined(xml, term.name, term.form)
  )
    .map(
      (term) =>
        `<term name="${term.name}"${term.form ? ` form="${term.form}"` : ""}>${term.value}</term>`
    )
    .join("");
  if (!terms) {
    return xml;
  }
  if (STYLE_EN_LOCALE_TERMS_REGEX.test(xml)) {
    return xml.replace(STYLE_EN_LOCALE_TERMS_REGEX, `$1${terms}`);
  }
  return xml.replace(
    STYLE_INFO_END,
    `${STYLE_INFO_END}<locale xml:lang="en"><terms>${terms}</terms></locale>`
  );
}

async function ensureStyleRegistered(
  slug: string,
  cslPath?: string
): Promise<void> {
  if (
    BUILTIN_STYLES.includes(slug as (typeof BUILTIN_STYLES)[number]) ||
    styleCache.has(slug)
  ) {
    return;
  }
  const path = getFetchPath(slug, cslPath);
  const url = `${CSL_STYLES_REPO}/${path}.csl`;
  const res = await fetch(url, { next: { revalidate: 86_400 } });
  if (!res.ok) {
    throw new Error(`Citation style "${slug}" not found.`);
  }
  const xml = adaptStyleXml(slug, await res.text());
  const config = plugins.config.get("@csl");
  if (config?.templates) {
    config.templates.add(slug, xml);
    styleCache.add(slug);
  }
}

const OPEN_LIBRARY_ISBN = "https://openlibrary.org/isbn";
const OPEN_LIBRARY_API = "https://openlibrary.org";

function _looksLikeDoiOrUrl(input: string): boolean {
  const trimmed = input.trim();
  return (
    trimmed.startsWith("10.") ||
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://")
  );
}

/** True if input is a DOI (10.xxx/...) or a doi.org URL. */
function isDoi(input: string): boolean {
  const trimmed = input.trim();
  if (DOI_PREFIX_REGEX.test(trimmed)) {
    return true;
  }
  try {
    const u = new URL(trimmed);
    return (
      (u.hostname === "doi.org" || u.hostname.endsWith(".doi.org")) &&
      DOI_PATHNAME_REGEX.test(u.pathname)
    );
  } catch {
    return false;
  }
}

/** True if input is an http(s) URL that is not a DOI. */
function isWebsiteUrl(input: string): boolean {
  const trimmed = input.trim();
  if (!(trimmed.startsWith("http://") || trimmed.startsWith("https://"))) {
    return false;
  }
  return !isDoi(trimmed);
}

/** ISBN-10 or ISBN-13 (digits and hyphens, optional X for 10) */
function looksLikeIsbn(input: string): boolean {
  const normalized = input.trim().replace(HYPHEN_REGEX, "");
  if (normalized.length === 10) {
    return ISBN_10_REGEX.test(normalized);
  }
  if (normalized.length === 13) {
    return ISBN_13_REGEX.test(normalized);
  }
  return false;
}

function normalizeIsbn(input: string): string {
  return input.trim().replace(HYPHEN_REGEX, "");
}

interface PmidLookup {
  /** The id passed to Cite.async (the plugin strips its own `pmid:`/`PMC` prefix). */
  id: string;
  forceType: "@pubmed/id" | "@pubmed/pmcid";
}

/** Extract a PubMed id from a pubmed.ncbi.nlm.nih.gov/<id> URL, if present. */
function parsePubmedUrl(trimmed: string): PmidLookup | null {
  try {
    const u = new URL(trimmed);
    if (
      u.hostname === PUBMED_URL_HOST ||
      u.hostname.endsWith(`.${PUBMED_URL_HOST}`)
    ) {
      const m = PUBMED_URL_PATH_REGEX.exec(u.pathname);
      if (m) {
        return { id: m[1], forceType: "@pubmed/id" };
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Detect a PubMed identifier — a bare PMID (`29083010`), a `pmid:`-prefixed PMID, a PMCID
 * (`PMC5334499`), or a pubmed.ncbi.nlm.nih.gov URL — and the citation-js type to force.
 * Returns null for anything else (DOIs, ISBNs, generic URLs all fail the shape checks).
 */
function parsePmid(input: string): PmidLookup | null {
  const trimmed = input.trim();
  if (PMCID_REGEX.test(trimmed)) {
    return { id: trimmed.toUpperCase(), forceType: "@pubmed/pmcid" };
  }
  const prefixed = PMID_PREFIX_REGEX.exec(trimmed);
  if (prefixed) {
    return { id: prefixed[1], forceType: "@pubmed/id" };
  }
  if (PMID_BARE_REGEX.test(trimmed)) {
    return { id: trimmed, forceType: "@pubmed/id" };
  }
  return parsePubmedUrl(trimmed);
}

/** True if input is a PubMed identifier (PMID, PMCID, or pubmed.ncbi.nlm.nih.gov URL). */
export function looksLikePmid(input: string): boolean {
  return parsePmid(input) !== null;
}

/** Extract a version-stripped arXiv id from an arxiv.org/abs|pdf URL, if present. */
function parseArxivUrl(trimmed: string): string | null {
  try {
    const u = new URL(trimmed);
    if (
      u.hostname === ARXIV_URL_HOST ||
      u.hostname.endsWith(`.${ARXIV_URL_HOST}`)
    ) {
      const m = ARXIV_URL_PATH_REGEX.exec(u.pathname);
      if (m) {
        return m[1];
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Detect an arXiv identifier — `arXiv:2202.01037`, a bare `2202.01037` (optionally `vN`),
 * or an arxiv.org/abs|pdf URL — and return the version-stripped id (current `YYMM.NNNNN`
 * scheme). Returns null for legacy-scheme ids (`hep-th/0107001`), PMIDs (no dot), ISBNs,
 * DOIs, and generic URLs, so it never hijacks an existing dispatch branch.
 */
function parseArxivId(input: string): string | null {
  const trimmed = input.trim();
  const prefixed = ARXIV_PREFIX_REGEX.exec(trimmed);
  if (prefixed) {
    return prefixed[1];
  }
  const bare = ARXIV_BARE_REGEX.exec(trimmed);
  if (bare) {
    return bare[1];
  }
  return parseArxivUrl(trimmed);
}

/** Map an arXiv id to the DataCite DOI arXiv auto-assigns it (`10.48550/arXiv.<id>`). */
export function arxivToDoi(id: string): string {
  return `10.48550/arXiv.${id}`;
}

/** True if input is an arXiv identifier (prefixed, bare current-scheme, or arxiv.org URL). */
export function looksLikeArxiv(input: string): boolean {
  return parseArxivId(input) !== null;
}

/**
 * Extract the 11-char video id from a YouTube watch / youtu.be / shorts / live / embed URL,
 * if present (issue #96). Returns null for non-video YouTube URLs (channels, playlists,
 * search) and every non-YouTube URL, so those still reach the generic HTML scraper.
 */
function parseYouTubeId(input: string): string | null {
  let u: URL;
  try {
    u = new URL(input.trim());
  } catch {
    return null;
  }
  const host = u.hostname.toLowerCase();
  if (host === YOUTUBE_SHORT_HOST || host.endsWith(`.${YOUTUBE_SHORT_HOST}`)) {
    const id = u.pathname.slice(1).split("/")[0];
    return YOUTUBE_ID_REGEX.test(id) ? id : null;
  }
  if (host === YOUTUBE_HOST || host.endsWith(`.${YOUTUBE_HOST}`)) {
    const watchId = u.searchParams.get("v");
    if (watchId && YOUTUBE_ID_REGEX.test(watchId)) {
      return watchId;
    }
    const pathMatch = YOUTUBE_PATH_REGEX.exec(u.pathname);
    if (pathMatch) {
      return pathMatch[1];
    }
  }
  return null;
}

/** True if input is a citable YouTube video URL (watch, youtu.be, shorts, live, embed). */
export function looksLikeYouTube(input: string): boolean {
  return parseYouTubeId(input) !== null;
}

/** Fetch edition by ISBN using Open Library ISBN API (not the legacy /api/books). */
async function fetchOpenLibraryIsbn(
  isbn: string
): Promise<Record<string, unknown>> {
  const url = `${OPEN_LIBRARY_ISBN}/${encodeURIComponent(isbn)}.json`;
  const res = await fetch(url, { next: { revalidate: 3600 } });
  if (res.status === 429) {
    throw new Error("Server responded with status code 429");
  }
  if (!res.ok) {
    throw new Error(`Open Library returned ${res.status}`);
  }
  return (await res.json()) as Record<string, unknown>;
}

/** Fetch work or author JSON from Open Library. */
async function fetchOpenLibrary(
  path: string
): Promise<Record<string, unknown>> {
  const url = `${OPEN_LIBRARY_API}${path}.json`;
  const res = await fetch(url, { next: { revalidate: 3600 } });
  if (res.status === 429) {
    throw new Error("Server responded with status code 429");
  }
  if (!res.ok) {
    return {};
  }
  return (await res.json()) as Record<string, unknown>;
}

function parseYear(publishDate: unknown): number | undefined {
  if (typeof publishDate !== "string") {
    return undefined;
  }
  const m = publishDate.match(YEAR_REGEX);
  return m ? Number.parseInt(m[0], 10) : undefined;
}

/** The first five author names on an edition's Open Library work record. */
async function fetchOpenLibraryAuthorNames(
  edition: Record<string, unknown>
): Promise<string[]> {
  const works = edition.works as Array<{ key: string }> | undefined;
  const workKey =
    Array.isArray(works) && works.length > 0 ? works[0]?.key : undefined;
  if (!workKey) {
    return [];
  }
  const work = await fetchOpenLibrary(workKey);
  const authors = work.authors as
    | Array<{ author?: { key: string } }>
    | undefined;
  if (!Array.isArray(authors)) {
    return [];
  }
  const keys = authors
    .slice(0, 5)
    .map((a) => a?.author?.key)
    .filter((k): k is string => typeof k === "string");
  const names: string[] = [];
  for (const key of keys) {
    const author = await fetchOpenLibrary(key);
    const name = author.name as string | undefined;
    if (typeof name === "string") {
      names.push(name);
    }
  }
  return names;
}

/** Resolve ISBN via Open Library ISBN API + work + authors, return CSL-JSON. */
async function isbnToCsl(isbn: string): Promise<Record<string, unknown>> {
  const edition = await fetchOpenLibraryIsbn(isbn);
  const mainTitle = (edition.title as string) ?? "Unknown";
  // Open Library keeps the subtitle apart; styles print "Title: Subtitle".
  const subtitle =
    typeof edition.subtitle === "string" ? edition.subtitle.trim() : "";
  const title = subtitle ? `${mainTitle}: ${subtitle}` : mainTitle;
  const publishers = edition.publishers as string[] | undefined;
  const publisher =
    Array.isArray(publishers) && publishers.length > 0
      ? publishers[0]
      : undefined;
  const publishDate = edition.publish_date;
  const year = parseYear(publishDate);
  const issued =
    year != null ? { "date-parts": [[year]] as number[][] } : undefined;

  const authorNames = await fetchOpenLibraryAuthorNames(edition);

  // Each Open Library author record is one name in natural order ("Thomas S. Kuhn"); the
  // same parser as the manual form splits it into parts so styles can invert and
  // initialize it, and still keeps an organization verbatim.
  const author =
    authorNames.length > 0 ? authorNames.map(parsePersonName) : undefined;

  return {
    type: "book",
    title,
    ...(publisher && { publisher }),
    ...(issued && { issued }),
    ...(author && author.length > 0 && { author }),
  };
}

const CITE_ASYNC_TIMEOUT_MS = 15_000;
const FETCH_TIMEOUT_MS = 8000;
const FETCH_MAX_BYTES = 512 * 1024;
const MAX_INPUT_LENGTH = 2000;
// Bulk BibTeX import accepts a whole .bib library, so it needs a much larger ceiling
// than the single-citation MAX_INPUT_LENGTH. Both are byte guards, not parser limits.
const MAX_IMPORT_LENGTH = 500_000;
const MAX_IMPORT_ENTRIES = 200;

/** Extract first meta content by property name (og:title, article:published_time, etc.). */
function getMetaContent(html: string, property: string): string | null {
  const escaped = property.replace(META_ESCAPE_REGEX, "\\$&");
  const patterns = [
    new RegExp(
      `<meta[^>]*property=["']${escaped}["'][^>]*content=["']([^"']*)["']`,
      "i"
    ),
    new RegExp(
      `<meta[^>]*content=["']([^"']*)["'][^>]*property=["']${escaped}["']`,
      "i"
    ),
    new RegExp(
      `<meta[^>]*name=["']${escaped}["'][^>]*content=["']([^"']*)["']`,
      "i"
    ),
    new RegExp(
      `<meta[^>]*content=["']([^"']*)["'][^>]*name=["']${escaped}["']`,
      "i"
    ),
  ];
  for (const re of patterns) {
    const m = html.match(re);
    if (m?.[1]) {
      return m[1].trim() || null;
    }
  }
  return null;
}

/** Parse ISO date string to CSL issued (date-parts). */
function parseIssued(
  dateStr: string
): { "date-parts": number[][] } | undefined {
  const s = dateStr.trim();
  const m = s.match(ISO_DATE_REGEX);
  if (m) {
    return {
      "date-parts": [
        [
          Number.parseInt(m[1], 10),
          Number.parseInt(m[2], 10),
          Number.parseInt(m[3], 10),
        ],
      ],
    };
  }
  const yearOnly = s.match(YEAR_ONLY_REGEX);
  if (yearOnly) {
    return { "date-parts": [[Number.parseInt(yearOnly[1], 10)]] };
  }
  return undefined;
}

/** Today in CSL date-parts for "accessed". */
function todayAccessed(): { "date-parts": number[][] } {
  const d = new Date();
  return {
    "date-parts": [[d.getFullYear(), d.getMonth() + 1, d.getDate()]],
  };
}

/**
 * Why a page fetch failed, in terms a reader can act on rather than in terms of
 * the wire. Every surface (`/api/cite`, `/api/v1/cite`, the MCP tool) reports the
 * message verbatim, so it has to make sense without a status-code lookup table.
 */
export type SourceFetchFailure =
  | "blocked"
  | "not-found"
  | "rate-limited"
  | "unavailable"
  | "timeout"
  | "network";

/** A page we could not read, carrying enough context to explain and to retry. */
export class SourceFetchError extends Error {
  readonly kind: SourceFetchFailure;
  /** Upstream HTTP status, or null when the request never got a response. */
  readonly status: number | null;
  /** The address the reader supplied, not the endpoint we happened to call. */
  readonly url: string;

  constructor(
    kind: SourceFetchFailure,
    status: number | null,
    url: string,
    message: string,
    // The underlying failure, kept for server logs — the reader-facing message
    // deliberately does not mention it.
    cause?: unknown
  ) {
    super(message, { cause });
    this.name = "SourceFetchError";
    this.kind = kind;
    this.status = status;
    this.url = url;
  }
}

/** Registrable host for error copy: "https://www.reuters.com/a/b" → "reuters.com". */
export function sourceHostLabel(url: string): string {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return host.startsWith("www.") ? host.slice(4) : host;
  } catch {
    return "The site";
  }
}

/** Bucket an HTTP status into the failure it means for someone citing a page. */
export function classifyFetchStatus(status: number): SourceFetchFailure {
  // 401/403 from a public article is a bot wall (DataDome, Cloudflare, Akamai),
  // not a real credentials problem — Reuters answers 401 to every client that
  // is not a browser it recognises. 402/451 are the paywall and legal-block
  // equivalents, and they leave the reader in the same position.
  if (status === 401 || status === 402 || status === 403 || status === 451) {
    return "blocked";
  }
  if (status === 404 || status === 410) {
    return "not-found";
  }
  if (status === 429) {
    return "rate-limited";
  }
  if (status === 408 || status >= 500) {
    return "unavailable";
  }
  return "network";
}

/**
 * The sentence a reader sees when a page cannot be read. Exported because this
 * copy is the actual product here: "URL returned 401" told a reader nothing,
 * least of all that the page they were looking at in their own browser was never
 * going to load for us and that typing the details in was the way forward.
 */
export function describeFetchFailure(
  kind: SourceFetchFailure,
  status: number | null,
  url: string
): string {
  const host = sourceHostLabel(url);
  const code = status === null ? "" : ` (HTTP ${status})`;
  switch (kind) {
    case "blocked":
      return `${host} blocked our request${code}. Many news and journal sites refuse automated readers even when the page opens normally in your browser, so we cannot read its details. Enter the title, author, and date yourself to cite it.`;
    case "not-found":
      return `${host} has no page at that address${code}. Check the link for a typo or a missing part, or cite the source by its DOI instead.`;
    case "rate-limited":
      return `${host} is rate-limiting requests${code}. Wait about a minute and try again.`;
    case "unavailable":
      return `${host} is temporarily unavailable${code}. Try again shortly, or enter the details yourself to cite it now.`;
    case "timeout":
      return `${host} took too long to respond. Try again, or enter the details yourself to cite it now.`;
    default:
      return `We could not read the page at ${host}${code}. Check the address, or enter the details yourself to cite it.`;
  }
}

/**
 * Fetch a page with a timeout and a size cap.
 *
 * `sourceUrl` is what the reader actually pasted; it differs from `url` when we
 * call a metadata endpoint on their behalf (YouTube oEmbed), and error copy
 * names their link rather than our plumbing.
 */
async function fetchHtmlWithTimeout(
  url: string,
  sourceUrl: string = url
): Promise<string> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const res = await fetch(url, {
      signal: controller.signal,
      headers: {
        "User-Agent":
          "CitationEasy/1.0 (https://www.citationeasy.com; metadata only)",
        // Sites that content-negotiate serve a bare API error, or nothing, to a
        // client that states no preference. Asking for HTML costs nothing and
        // wins back the pages that were failing for that reason alone.
        Accept:
          "text/html,application/xhtml+xml,application/json;q=0.9,*/*;q=0.8",
        "Accept-Language": "en-US,en;q=0.9",
      },
      next: { revalidate: 3600 },
    });
    clearTimeout(timeout);

    if (!res.ok) {
      const kind = classifyFetchStatus(res.status);
      throw new SourceFetchError(
        kind,
        res.status,
        sourceUrl,
        describeFetchFailure(kind, res.status, sourceUrl)
      );
    }

    const buf = await res.arrayBuffer();
    const slicedBuf =
      buf.byteLength > FETCH_MAX_BYTES ? buf.slice(0, FETCH_MAX_BYTES) : buf;
    return new TextDecoder("utf-8", { fatal: false }).decode(slicedBuf);
  } catch (err) {
    clearTimeout(timeout);
    if (err instanceof SourceFetchError) {
      throw err;
    }
    if (err instanceof Error && err.name === "AbortError") {
      throw new SourceFetchError(
        "timeout",
        null,
        sourceUrl,
        describeFetchFailure("timeout", null, sourceUrl)
      );
    }
    // DNS failure, refused connection, TLS error: no response, so no status.
    throw new SourceFetchError(
      "network",
      null,
      sourceUrl,
      describeFetchFailure("network", null, sourceUrl),
      err
    );
  }
}

/** Extract title from HTML using multiple meta tag fallbacks */
function extractTitle(html: string): string | null {
  return (
    getMetaContent(html, "og:title") ??
    getMetaContent(html, "twitter:title") ??
    getMetaContent(html, "article:headline")
  );
}

/** Extract published date from HTML using multiple meta tag fallbacks */
function extractPublishedDate(html: string): string | null {
  return (
    getMetaContent(html, "article:published_time") ??
    getMetaContent(html, "datePublished") ??
    getMetaContent(html, "pubdate")
  );
}

/** Extract author from HTML using multiple meta tag fallbacks */
function extractAuthor(html: string): string | null {
  return (
    getMetaContent(html, "article:author") ??
    getMetaContent(html, "author") ??
    getMetaContent(html, "twitter:creator")
  );
}

/** A single bibliographic record distilled from one metadata source. */
interface ScholarlyMeta {
  title?: string;
  authors?: string[];
  date?: string;
  journalTitle?: string;
  volume?: string;
  issue?: string;
  page?: string;
  DOI?: string;
  publisher?: string;
}

interface MetaPair {
  name: string;
  content: string;
}

/** Parse every <meta> tag once into lowercased name/content pairs (order-preserving). */
function parseMetaPairs(html: string): MetaPair[] {
  const tags = html.match(META_TAG_REGEX) ?? [];
  const pairs: MetaPair[] = [];
  for (const tag of tags) {
    const name = tag.match(META_NAME_ATTR_REGEX)?.[1]?.trim();
    const content = tag.match(META_CONTENT_ATTR_REGEX)?.[1]?.trim();
    if (name && content) {
      pairs.push({ name: name.toLowerCase(), content });
    }
  }
  return pairs;
}

/** All content values for a meta name (Highwire citation_author is multi-valued). */
function metaValues(pairs: MetaPair[], name: string): string[] {
  const lower = name.toLowerCase();
  return pairs.filter((p) => p.name === lower).map((p) => p.content);
}

/** First content value for a meta name, or undefined. */
function metaValue(pairs: MetaPair[], name: string): string | undefined {
  return metaValues(pairs, name)[0];
}

/** Join Highwire first/last page into a CSL page range. */
function highwirePage(
  first: string | undefined,
  last: string | undefined
): string | undefined {
  if (first && last) {
    return `${first}-${last}`;
  }
  return first ?? last;
}

/** Read the Highwire (citation_*) meta family — the scholarly/Scholar standard. */
function readHighwire(pairs: MetaPair[]): ScholarlyMeta {
  return {
    title: metaValue(pairs, "citation_title"),
    authors: metaValues(pairs, "citation_author"),
    date:
      metaValue(pairs, "citation_publication_date") ??
      metaValue(pairs, "citation_date"),
    journalTitle: metaValue(pairs, "citation_journal_title"),
    volume: metaValue(pairs, "citation_volume"),
    issue: metaValue(pairs, "citation_issue"),
    page: highwirePage(
      metaValue(pairs, "citation_firstpage"),
      metaValue(pairs, "citation_lastpage")
    ),
    DOI: metaValue(pairs, "citation_doi"),
    publisher: metaValue(pairs, "citation_publisher"),
  };
}

/** Read the Dublin Core (DC.*) meta family — the fallback when Highwire is absent. */
function readDublinCore(pairs: MetaPair[]): ScholarlyMeta {
  return {
    title: metaValue(pairs, "dc.title"),
    authors: metaValues(pairs, "dc.creator"),
    date: metaValue(pairs, "dc.issued") ?? metaValue(pairs, "dc.date"),
    publisher: metaValue(pairs, "dc.publisher"),
  };
}

/** True if a JSON-LD @type is (or includes) an Article-like type. */
function isArticleType(type: unknown): boolean {
  if (typeof type === "string") {
    return ARTICLE_TYPE_REGEX.test(type);
  }
  if (Array.isArray(type)) {
    return type.some(
      (t) => typeof t === "string" && ARTICLE_TYPE_REGEX.test(t)
    );
  }
  return false;
}

/** Find the first Article-like node in a parsed JSON-LD value (object or array). */
function findArticleNode(data: unknown): Record<string, unknown> | null {
  const candidates = Array.isArray(data) ? data : [data];
  for (const c of candidates) {
    if (
      c &&
      typeof c === "object" &&
      isArticleType((c as Record<string, unknown>)["@type"])
    ) {
      return c as Record<string, unknown>;
    }
  }
  return null;
}

/** Extract one author name from a JSON-LD author entry (string or { name }). */
function jsonLdAuthorName(a: unknown): string | undefined {
  if (typeof a === "string") {
    return a.trim() || undefined;
  }
  if (a && typeof a === "object") {
    const name = (a as Record<string, unknown>).name;
    if (typeof name === "string") {
      return name.trim() || undefined;
    }
  }
  return undefined;
}

/** Normalize a JSON-LD author (string, object, or array) to a list of names. */
function jsonLdAuthors(author: unknown): string[] {
  const list = Array.isArray(author) ? author : [author];
  return list.map(jsonLdAuthorName).filter((n): n is string => Boolean(n));
}

/** Prefer headline, fall back to name, for a JSON-LD Article title. */
function jsonLdTitle(node: Record<string, unknown>): string | undefined {
  const headline = node.headline;
  if (typeof headline === "string" && headline.trim()) {
    return headline.trim();
  }
  const name = node.name;
  return typeof name === "string" ? name.trim() || undefined : undefined;
}

/** Read the first JSON-LD Article block (headline/author/datePublished). */
function readJsonLd(html: string): ScholarlyMeta {
  for (const m of html.matchAll(JSON_LD_SCRIPT_REGEX)) {
    let data: unknown;
    try {
      data = JSON.parse(m[1].trim());
    } catch {
      continue;
    }
    const node = findArticleNode(data);
    if (node) {
      const date = node.datePublished;
      return {
        title: jsonLdTitle(node),
        authors: jsonLdAuthors(node.author),
        date: typeof date === "string" ? date : undefined,
      };
    }
  }
  return {};
}

/** First non-empty string for a field across sources, in priority order. */
function pickField(
  sources: ScholarlyMeta[],
  key: keyof ScholarlyMeta
): string | undefined {
  for (const s of sources) {
    const v = s[key];
    if (typeof v === "string" && v) {
      return v;
    }
  }
  return undefined;
}

/** First non-empty author list across sources, in priority order. */
function pickAuthors(sources: ScholarlyMeta[]): string[] | undefined {
  for (const s of sources) {
    if (s.authors && s.authors.length > 0) {
      return s.authors;
    }
  }
  return undefined;
}

/** Merge Highwire > Dublin Core > JSON-LD field-by-field into one record. */
function readScholarlyMeta(html: string): ScholarlyMeta {
  const pairs = parseMetaPairs(html);
  const sources = [
    readHighwire(pairs),
    readDublinCore(pairs),
    readJsonLd(html),
  ];
  return {
    title: pickField(sources, "title"),
    authors: pickAuthors(sources),
    date: pickField(sources, "date"),
    journalTitle: pickField(sources, "journalTitle"),
    volume: pickField(sources, "volume"),
    issue: pickField(sources, "issue"),
    page: pickField(sources, "page"),
    DOI: pickField(sources, "DOI"),
    publisher: pickField(sources, "publisher"),
  };
}

/** Parse a scholarly meta date — accepts slash dates, then ISO/year via parseIssued. */
function parseMetaDate(
  dateStr: string | undefined
): { "date-parts": number[][] } | undefined {
  if (!dateStr) {
    return undefined;
  }
  const s = dateStr.trim();
  const ymd = s.match(SLASH_DATE_REGEX);
  if (ymd) {
    return {
      "date-parts": [
        [
          Number.parseInt(ymd[1], 10),
          Number.parseInt(ymd[2], 10),
          Number.parseInt(ymd[3], 10),
        ],
      ],
    };
  }
  const ym = s.match(SLASH_YEAR_MONTH_REGEX);
  if (ym) {
    return {
      "date-parts": [[Number.parseInt(ym[1], 10), Number.parseInt(ym[2], 10)]],
    };
  }
  return parseIssued(s);
}

/** Map a list of individual name strings to structured CSL persons (#2 fix). */
function personsFromNames(
  names: string[] | undefined
): CslAuthor[] | undefined {
  if (!names || names.length === 0) {
    return undefined;
  }
  return names.map(parsePersonName);
}

/**
 * Build a CSL entry from a page's HTML (issue #7). Prefers real bibliographic
 * metadata — Highwire (citation_*), Dublin Core (DC.*), then JSON-LD Article —
 * and falls back to Open Graph / Twitter so OG-only pages never regress. Every
 * extracted author is routed through makePerson so URL-cited names render
 * style-inverted (APA "Doe, J."), closing the #2 gap on this input path.
 */
function parseHtmlToCsl(html: string, url: string): Record<string, unknown> {
  const meta = readScholarlyMeta(html);
  const ogAuthor = extractAuthor(html);
  const siteName = getMetaContent(html, "og:site_name");

  const title = meta.title ?? extractTitle(html) ?? url;
  const issued = parseMetaDate(
    meta.date ?? extractPublishedDate(html) ?? undefined
  );
  const author = meta.authors
    ? personsFromNames(meta.authors)
    : makePerson(ogAuthor ?? undefined);
  const containerTitle = meta.journalTitle ?? siteName ?? undefined;
  const isJournal = Boolean(meta.journalTitle || meta.DOI);

  const csl: Record<string, unknown> = {
    type: isJournal ? "article-journal" : "webpage",
    title,
    URL: url,
    accessed: todayAccessed(),
  };
  if (issued) {
    csl.issued = issued;
  }
  if (author && author.length > 0) {
    csl.author = author;
  }
  addIfPresent(csl, "container-title", containerTitle);
  addIfPresent(csl, "volume", meta.volume);
  addIfPresent(csl, "issue", meta.issue);
  addIfPresent(csl, "page", meta.page);
  addIfPresent(csl, "DOI", meta.DOI);
  addIfPresent(csl, "publisher", meta.publisher);
  return csl;
}

/** Fetch URL and extract metadata from HTML (scholarly meta, JSON-LD, Open Graph). */
async function urlToCsl(url: string): Promise<Record<string, unknown>> {
  const html = await fetchHtmlWithTimeout(url);
  return parseHtmlToCsl(html, url);
}

/** Build a CSL entry from raw HTML and a URL — exported for offline golden tests. */
export function htmlToCslEntry(
  html: string,
  url: string
): Record<string, unknown> {
  return parseHtmlToCsl(html, url);
}

/**
 * Map a YouTube oEmbed JSON response to a CSL `motion_picture` record (issue #96) — exported
 * for offline tests. The channel (`author_name`) becomes a verbatim `{ literal }` group author
 * (matching the documented APA exemplar "Khan Academy. (…) Title [Video]. YouTube. URL"), and
 * `genre: "Video"` drives the bracketed "[Video]" descriptor across APA/MLA/Chicago. oEmbed
 * returns no upload date, so `issued` is intentionally left for the user to fill in the editor.
 */
export function youtubeOEmbedToCsl(
  oembed: Record<string, unknown>,
  url: string
): Record<string, unknown> {
  const rawTitle = typeof oembed.title === "string" ? oembed.title.trim() : "";
  const authorName =
    typeof oembed.author_name === "string" ? oembed.author_name.trim() : "";
  const provider =
    typeof oembed.provider_name === "string" && oembed.provider_name.trim()
      ? oembed.provider_name.trim()
      : "YouTube";
  const csl: Record<string, unknown> = {
    type: "motion_picture",
    title: rawTitle || url,
    "container-title": provider,
    genre: "Video",
    URL: url,
  };
  if (authorName) {
    csl.author = [{ literal: authorName }];
  }
  return csl;
}

/** Fetch a YouTube video's keyless oEmbed metadata and map it to a CSL record. */
async function youtubeToCsl(url: string): Promise<Record<string, unknown>> {
  const oembedUrl = `${YOUTUBE_OEMBED_ENDPOINT}?url=${encodeURIComponent(url)}&format=json`;
  const text = await fetchHtmlWithTimeout(oembedUrl, url);
  const json = JSON.parse(text) as Record<string, unknown>;
  return youtubeOEmbedToCsl(json, url);
}

export function isAllowedStyle(style: string): boolean {
  return typeof style === "string" && isValidStyleSlug(style.trim());
}

/** Flat shape for editing an entry in the UI (extended for wizard). */
export interface EditableEntry {
  title?: string;
  author?: string;
  issued?: string;
  URL?: string;
  publisher?: string;
  containerTitle?: string;
  type?: string;
  // Extended fields for wizard
  volume?: string;
  issue?: string;
  page?: string;
  DOI?: string;
  ISBN?: string;
  edition?: string;
  accessed?: string;
  section?: string;
  duration?: string;
  number?: string;
  genre?: string;
  editor?: string;
  eventPlace?: string;
  /** Translator(s), parsed like `author` (CSL `translator`). */
  translator?: string;
  /** Interviewer(s) of an interview (CSL `interviewer`). */
  interviewer?: string;
  /** Software or app version, e.g. "4.3.1" (CSL `version`). */
  version?: string;
  /** Court or issuing authority, e.g. "D. Kan." (CSL `authority`). */
  authority?: string;
  /** Title of the work a review discusses (CSL `reviewed-title`). */
  reviewedTitle?: string;
  /** Author(s) of the reviewed work (CSL `reviewed-author`). */
  reviewedAuthor?: string;
}

interface CslAuthor {
  family?: string;
  given?: string;
  suffix?: string;
  "non-dropping-particle"?: string;
  "dropping-particle"?: string;
  literal?: string;
}
interface CslIssued {
  "date-parts"?: number[][];
}

function datePartsToString(
  dateParts: number[][] | undefined
): string | undefined {
  const parts = dateParts?.[0];
  if (!parts) {
    return undefined;
  }
  if (parts.length === 3) {
    return `${parts[0]}-${String(parts[1]).padStart(2, "0")}-${String(parts[2]).padStart(2, "0")}`;
  }
  if (parts.length === 2) {
    return `${parts[0]}-${String(parts[1]).padStart(2, "0")}`;
  }
  if (parts.length === 1) {
    return String(parts[0]);
  }
  return undefined;
}

/**
 * Write one CSL name as "Given [particles] Family [Suffix]", the form parsePersonName
 * reads back into the same parts: lowercase particles in NAME_PARTICLES return as the
 * non-dropping particle and allow-listed suffixes as `suffix`. Direct order, because an
 * inverted "Family, Given" is ambiguous inside the comma-separated author list (see
 * splitTwoCommaParts). The string has no slot for a dropping particle, so it is written
 * before the surname and comes back as a non-dropping one. Any other suffix is left out:
 * the parser reads an unrecognized final token as the family name.
 */
function cslNameToString(name: CslAuthor | null | undefined): string {
  if (name?.literal) {
    return name.literal;
  }
  const particle = name?.["non-dropping-particle"];
  const family =
    particle && name?.family && ATTACHED_PARTICLE_REGEX.test(particle)
      ? `${particle}${name.family}`
      : [particle, name?.family].filter(Boolean).join(" ");
  const suffix = name?.suffix?.trim();
  return [
    name?.given,
    name?.["dropping-particle"],
    family,
    suffix && isNameSuffix(suffix) ? suffix : undefined,
  ]
    .filter(Boolean)
    .join(" ");
}

/** Flatten a CSL name list into the comma-separated `author`/`editor` string. */
function cslNamesToString(names: unknown): string | undefined {
  if (!Array.isArray(names) || names.length === 0) {
    return undefined;
  }
  return (names as (CslAuthor | null)[])
    .map(cslNameToString)
    .filter(Boolean)
    .join(", ");
}

export function cslToEditableEntry(
  csl: Record<string, unknown>
): EditableEntry {
  const authorStr = cslNamesToString(csl.author);
  const editorStr = cslNamesToString(csl.editor);

  const issued = csl.issued as CslIssued | undefined;
  const issuedStr = datePartsToString(issued?.["date-parts"]);

  const accessed = csl.accessed as CslIssued | undefined;
  const accessedStr = datePartsToString(accessed?.["date-parts"]);

  return {
    title: (csl.title as string) ?? undefined,
    author: authorStr,
    issued: issuedStr,
    URL: (csl.URL as string) ?? undefined,
    publisher: (csl.publisher as string) ?? undefined,
    containerTitle: (csl["container-title"] as string) ?? undefined,
    type: (csl.type as string) ?? undefined,
    // Extended fields
    volume: (csl.volume as string) ?? undefined,
    issue: (csl.issue as string) ?? undefined,
    page: (csl.page as string) ?? undefined,
    DOI: (csl.DOI as string) ?? undefined,
    ISBN: (csl.ISBN as string) ?? undefined,
    edition: (csl.edition as string) ?? undefined,
    accessed: accessedStr,
    section: (csl.section as string) ?? undefined,
    number: (csl.number as string) ?? undefined,
    genre: (csl.genre as string) ?? undefined,
    editor: editorStr,
    eventPlace: (csl["event-place"] as string) ?? undefined,
    translator: cslNamesToString(csl.translator),
    interviewer: cslNamesToString(csl.interviewer),
    version: (csl.version as string) ?? undefined,
    authority: (csl.authority as string) ?? undefined,
    reviewedTitle: (csl["reviewed-title"] as string) ?? undefined,
    reviewedAuthor: cslNamesToString(csl["reviewed-author"]),
  };
}

function parseDateString(
  dateStr: string | undefined
): { "date-parts": number[][] } | undefined {
  if (!dateStr?.trim()) {
    return undefined;
  }
  const s = dateStr.trim();
  const m = s.match(ISO_DATE_FULL_REGEX);
  if (m) {
    return {
      "date-parts": [
        [
          Number.parseInt(m[1], 10),
          Number.parseInt(m[2], 10),
          Number.parseInt(m[3], 10),
        ],
      ],
    };
  }
  const ym = s.match(YEAR_MONTH_REGEX);
  if (ym) {
    return {
      "date-parts": [[Number.parseInt(ym[1], 10), Number.parseInt(ym[2], 10)]],
    };
  }
  const y = s.match(YEAR_ONLY_REGEX);
  if (y) {
    return { "date-parts": [[Number.parseInt(y[1], 10)]] };
  }
  return undefined;
}

function isParticle(token: string): boolean {
  return NAME_PARTICLES.has(token);
}

/** True for a generational suffix the name parser lifts into CSL `suffix` (Jr., III). */
export function isNameSuffix(token: string): boolean {
  return NAME_SUFFIX_REGEX.test(token);
}

/** Assemble a CSL name from parts, omitting any that are empty (preserves the byte-for-
 *  byte `{ family, given }` shape for names with no suffix/particle). */
function buildPerson(parts: {
  family: string;
  given: string;
  particle?: string;
  suffix?: string;
}): CslAuthor {
  const person: CslAuthor = { family: parts.family.trim() };
  const given = parts.given.trim();
  if (given) {
    person.given = given;
  }
  if (parts.particle) {
    person["non-dropping-particle"] = parts.particle;
  }
  if (parts.suffix) {
    person.suffix = parts.suffix;
  }
  return person;
}

/** Lift a trailing generational suffix (Jr./III) off a token list, keeping at least
 *  `minRemaining` tokens behind it. */
function takeTrailingSuffix(
  tokens: string[],
  minRemaining: number
): { rest: string[]; suffix?: string } {
  const last = tokens.at(-1);
  if (tokens.length > minRemaining && last && NAME_SUFFIX_REGEX.test(last)) {
    return { rest: tokens.slice(0, -1), suffix: last };
  }
  return { rest: tokens };
}

/** Fold a run of lowercase particles immediately *before* the family name (the direct
 *  "Ludwig van Beethoven" shape) into a non-dropping-particle. */
function takeTrailingParticle(tokens: string[]): {
  particle?: string;
  rest: string[];
} {
  let i = tokens.length;
  while (i > 0 && isParticle(tokens[i - 1])) {
    i--;
  }
  if (i === tokens.length) {
    return { rest: tokens };
  }
  return { particle: tokens.slice(i).join(" "), rest: tokens.slice(0, i) };
}

/** Fold a run of leading lowercase particles inside an inverted family part (the
 *  "van der Westhuizen, R." shape) into a non-dropping-particle. */
function takeLeadingParticle(tokens: string[]): {
  particle?: string;
  rest: string[];
} {
  let i = 0;
  while (i < tokens.length - 1 && isParticle(tokens[i])) {
    i++;
  }
  if (i === 0) {
    return { rest: tokens };
  }
  return { particle: tokens.slice(0, i).join(" "), rest: tokens.slice(i) };
}

/** Parse a single inverted "Family, Given[, Suffix]" name into structured CSL parts. */
function parseInvertedName(family: string, given: string): CslAuthor {
  const givenTokens = given.trim().split(NAME_PART_SPLIT_REGEX).filter(Boolean);
  const { rest: givenRest, suffix } = takeTrailingSuffix(givenTokens, 0);
  const familyTokens = family
    .trim()
    .split(NAME_WHITESPACE_REGEX)
    .filter(Boolean);
  const { particle, rest: familyRest } = takeLeadingParticle(familyTokens);
  return buildPerson({
    family: familyRest.join(" "),
    given: givenRest.join(" "),
    particle,
    suffix,
  });
}

/** Parse a single "First [Middle] [particle] Last [Suffix]" name; the last non-suffix,
 *  non-particle token is the family name. */
function parseDirectName(name: string): CslAuthor {
  const tokens = name.split(NAME_WHITESPACE_REGEX);
  if (tokens.length < 2 || ORGANIZATION_NAME_REGEX.test(name)) {
    return { literal: name };
  }
  const { rest, suffix } = takeTrailingSuffix(tokens, 1);
  const family = rest.at(-1) ?? name;
  const { particle, rest: givenRest } = takeTrailingParticle(rest.slice(0, -1));
  return buildPerson({
    family,
    given: givenRest.join(" "),
    particle,
    suffix,
  });
}

/** Parse one person's name into structured CSL parts (with literal fallback). */
function parsePersonName(name: string): CslAuthor {
  const braced = name.trim().match(BRACED_SINGLE_NAME_REGEX);
  if (braced) {
    return { literal: braced[1].trim() };
  }
  const commaIndex = name.indexOf(",");
  if (commaIndex === -1) {
    return parseDirectName(name);
  }
  const family = name.slice(0, commaIndex);
  const given = name.slice(commaIndex + 1);
  if (family.trim() && given.trim()) {
    return parseInvertedName(family, given);
  }
  return { literal: name.trim() };
}

/**
 * Decide whether a comma-separated, two-element string is an inverted single name
 * ("Kuhn, Thomas S.") or two direct names ("Erich Gamma, Richard Helm"). Two direct
 * names is assumed only when BOTH halves are multi-token; otherwise it is inverted.
 */
function splitTwoCommaParts(first: string, second: string): string[] {
  if (
    BRACED_PLACEHOLDER_ONLY_REGEX.test(first.trim()) ||
    BRACED_PLACEHOLDER_ONLY_REGEX.test(second.trim())
  ) {
    return [first, second];
  }
  const bothMultiToken =
    NAME_WHITESPACE_REGEX.test(first.trim()) &&
    NAME_WHITESPACE_REGEX.test(second.trim());
  return bothMultiToken ? [first, second] : [`${first},${second}`];
}

/**
 * Split an author string into individual name strings, handling list ambiguity. Braced
 * group names ("{Smith & Jones, LLP}") are masked first so nothing inside them splits.
 */
export function splitAuthorList(value: string): string[] {
  const braced: string[] = [];
  const masked = value.replace(BRACED_NAME_REGEX, (match) => {
    braced.push(match);
    return `\uE000${braced.length - 1}\uE000`;
  });
  if (braced.length === 0) {
    return splitMaskedAuthorList(value);
  }
  return splitMaskedAuthorList(masked).map((part) =>
    part.replace(
      BRACED_PLACEHOLDER_REGEX,
      (_match, index: string) => braced[Number(index)] ?? ""
    )
  );
}

function splitMaskedAuthorList(value: string): string[] {
  const explicit = value
    .split(AUTHOR_LIST_SEPARATOR_REGEX)
    .map((s) => s.trim())
    .filter(Boolean);
  if (explicit.length > 1) {
    // A chunk between explicit separators can itself be a comma list: "Watson, J. D.,
    // Crick, F. H. C., & Wilkins, M." or "Erich Gamma, Richard Helm and Ralph Johnson".
    return explicit.flatMap((chunk) => {
      const names = splitCommaList(chunk);
      return names.length > 1 ? names : [chunk];
    });
  }
  return splitCommaList(value);
}

/** True for an initial token such as "J.", "J", "J.-P." or "H.C.". */
function isInitialToken(token: string): boolean {
  return INITIAL_TOKEN_REGEX.test(token);
}

/** A given-name part of an inverted name: initials, optionally with given names. */
function looksLikeGivenPart(part: string): boolean {
  const tokens = part.split(NAME_WHITESPACE_REGEX).filter(Boolean);
  return (
    tokens.some(isInitialToken) &&
    tokens.every(
      (token) => isInitialToken(token) || CAPITALIZED_WORD_REGEX.test(token)
    )
  );
}

/**
 * Pair the parts of an inverted list typed the way APA prints one — "Watson, J. D.,
 * Crick, F. H. C." — into "Family, Given" names. Applies only when every second part
 * carries initials and no family part does, so a list of direct names never pairs.
 */
function pairInvertedParts(parts: string[]): string[] | null {
  if (parts.length < 4 || parts.length % 2 !== 0) {
    return null;
  }
  const names: string[] = [];
  for (let i = 0; i < parts.length; i += 2) {
    const family = parts[i];
    const given = parts[i + 1];
    const familyHasInitial = family
      .split(NAME_WHITESPACE_REGEX)
      .some(isInitialToken);
    if (familyHasInitial || !looksLikeGivenPart(given)) {
      return null;
    }
    names.push(`${family},${given}`);
  }
  return names;
}

function splitCommaList(value: string): string[] {
  const commaParts = value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  // Reattach a trailing generational suffix to the preceding name so "King, Martin
  // Luther, Jr." is one author (with suffix "Jr."), not three with a phantom "Jr.".
  const lastPart = commaParts.at(-1);
  if (commaParts.length >= 2 && lastPart && NAME_SUFFIX_REGEX.test(lastPart)) {
    commaParts.pop();
    const preceding = commaParts.pop() ?? "";
    commaParts.push(`${preceding} ${lastPart}`);
  }
  if (commaParts.length >= 3) {
    return pairInvertedParts(commaParts) ?? commaParts;
  }
  if (commaParts.length === 2) {
    return splitTwoCommaParts(commaParts[0], commaParts[1]);
  }
  return [value.trim()];
}

/**
 * Parse an author/editor string into structured CSL name parts so citeproc can apply
 * each style's inversion/initialization rules (e.g. APA "Kuhn, T. S.").
 *
 * Handles "First Last", "First M. Last", already-inverted "Last, First", and
 * multi-author input separated by ";", "&", " and ", or commas. Generational suffixes
 * (Jr./Sr./II–IV/V) become CSL `suffix` and lowercase surname particles (van/de/von…)
 * become `non-dropping-particle`, via the conservative allow-lists above. Names that look
 * organizational (or are a single token) fall back to a verbatim { literal } so they are
 * not wrongly inverted. Non-Latin ordering and dropping-particle nuances are out of scope.
 */
function makePerson(value: string | undefined): CslAuthor[] | undefined {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }
  // An empty brace pair — the form's group-author box ticked before typing — is no name.
  const names = splitAuthorList(trimmed)
    .map(parsePersonName)
    .filter((name) => name.literal !== "");
  return names.length > 0 ? names : undefined;
}

/** Add a parsed name list to CSL if the string is non-empty. */
function addNamesIfPresent(
  csl: Record<string, unknown>,
  cslKey: string,
  value: string | undefined
): void {
  const names = makePerson(value);
  if (names) {
    csl[cslKey] = names;
  }
}

/** Add field to CSL if value exists and is non-empty */
function addIfPresent(
  csl: Record<string, unknown>,
  cslKey: string,
  value: string | undefined
): void {
  const trimmed = value?.trim();
  if (trimmed) {
    csl[cslKey] = trimmed;
  }
}

/**
 * The CSL type for an entry. The form's "Edited collection / anthology" option is
 * `collection`, which CSL 1.0.2 defines as an archival collection; an edited volume is a
 * `book` with editors, and styles render it as one (e.g. Chicago's parenthesized
 * publication facts in a note).
 */
function cslTypeForEntry(entry: EditableEntry): string {
  const type = entry.type || "webpage";
  if (type === "collection" && entry.editor?.trim()) {
    return "book";
  }
  return type;
}

export function editableEntryToCsl(
  entry: EditableEntry
): Record<string, unknown> {
  const author = makePerson(entry.author);
  const editor = makePerson(entry.editor);
  const issued = parseDateString(entry.issued);
  const accessed = parseDateString(entry.accessed);

  const csl: Record<string, unknown> = {
    type: cslTypeForEntry(entry),
    title: entry.title?.trim() || "Untitled",
  };

  if (author) {
    csl.author = author;
  }
  if (editor) {
    csl.editor = editor;
  }
  addNamesIfPresent(csl, "translator", entry.translator);
  addNamesIfPresent(csl, "interviewer", entry.interviewer);
  addNamesIfPresent(csl, "reviewed-author", entry.reviewedAuthor);
  if (issued) {
    csl.issued = issued;
  }
  if (accessed) {
    csl.accessed = accessed;
  }

  // Core fields
  addIfPresent(csl, "URL", entry.URL);
  addIfPresent(csl, "publisher", entry.publisher);
  addIfPresent(csl, "container-title", entry.containerTitle);

  // Extended fields
  addIfPresent(csl, "volume", entry.volume);
  addIfPresent(csl, "issue", entry.issue);
  addIfPresent(csl, "page", entry.page);
  addIfPresent(csl, "DOI", entry.DOI);
  addIfPresent(csl, "ISBN", entry.ISBN);
  addIfPresent(csl, "edition", entry.edition);
  addIfPresent(csl, "section", entry.section);
  addIfPresent(csl, "number", entry.number);
  addIfPresent(csl, "genre", entry.genre);
  addIfPresent(csl, "event-place", entry.eventPlace);
  addIfPresent(csl, "version", entry.version);
  addIfPresent(csl, "authority", entry.authority);
  addIfPresent(csl, "reviewed-title", entry.reviewedTitle);

  return csl;
}

// A domain name used as a title or site name ("Merriam-Webster.com", "BBC.co.uk"). Styles
// that title-case a site name would print "Merriam-Webster.Com", so the domain is wrapped
// in citeproc's no-case markup, which every style honours and the output omits.
const DOMAIN_NAME_REGEX =
  /(?<![\p{L}\d.@/-])[\p{L}\d][\p{L}\d-]*(?:\.[\p{L}\d-]+)*\.[a-z]{2,6}(?![\p{L}\d.-])/gu;
const NOCASE_FIELDS = [
  "title",
  "container-title",
  "publisher",
  "collection-title",
];
const APA_STYLE_SLUG = "apa";
// APA 7 (10.2): the format, platform or device an e-book was read on is not included.
const EBOOK_FORMAT_REGEX =
  /^(?:(?:amazon\s+)?kindle|nook|kobo|apple\s+books|ibooks|google\s+play(?:\s+books)?|epub|pdf|e-?book|electronic\s+book)(?:\s+(?:edition|ed\.?|version|e-?book|book))?$/i;
const NAME_COMPARE_STRIP_REGEX = /[\s.,]+/g;
// A version given as a bare number ("4.3.1", "2") rather than with its label.
const VERSION_NUMBER_REGEX = /^\d/;

function protectDomainNames(csl: Record<string, unknown>): void {
  for (const field of NOCASE_FIELDS) {
    const value = csl[field];
    if (typeof value === "string" && DOMAIN_NAME_REGEX.test(value)) {
      DOMAIN_NAME_REGEX.lastIndex = 0;
      csl[field] = value.replace(
        DOMAIN_NAME_REGEX,
        (domain) => `<span class="nocase">${domain}</span>`
      );
    }
    DOMAIN_NAME_REGEX.lastIndex = 0;
  }
}

function comparableName(value: unknown): string {
  return typeof value === "string"
    ? value.toLowerCase().replace(NAME_COMPARE_STRIP_REGEX, "")
    : "";
}

/** The single group author's name, when the work has exactly one and it is a literal. */
function soleLiteralAuthor(csl: Record<string, unknown>): string {
  const authors = csl.author;
  if (Array.isArray(authors) && authors.length === 1) {
    return comparableName((authors[0] as CslAuthor | null)?.literal);
  }
  return "";
}

/**
 * APA 7 data rules the CSL style cannot express:
 * - an online video's platform is its publisher (10.12: "[Video]. YouTube. URL"), but the
 *   generator's container field puts it in `container-title`, which APA prints "In YouTube";
 * - a publisher (or a web page's site name) that repeats the group author is omitted
 *   (9.24, 9.25);
 * - an e-book's reading format or device is omitted (10.2).
 */
function adaptForApa(csl: Record<string, unknown>): void {
  const hasContainerContributor = [
    "editor",
    "collection-editor",
    "container-author",
  ].some((key) => csl[key] !== undefined);
  if (
    csl.type === "motion_picture" &&
    csl["container-title"] &&
    !csl.publisher &&
    !hasContainerContributor
  ) {
    csl.publisher = csl["container-title"];
    Reflect.deleteProperty(csl, "container-title");
  }
  const author = soleLiteralAuthor(csl);
  if (author && comparableName(csl.publisher) === author) {
    Reflect.deleteProperty(csl, "publisher");
  }
  if (
    author &&
    (csl.type === "webpage" || csl.type === "post") &&
    comparableName(csl["container-title"]) === author
  ) {
    Reflect.deleteProperty(csl, "container-title");
  }
  // APA 7 (10.10): "(Version 3.3.070)". citeproc only adds the label to a plain number,
  // so a dotted version ("4.3.1") printed bare.
  const version = typeof csl.version === "string" ? csl.version.trim() : "";
  if (version && VERSION_NUMBER_REGEX.test(version)) {
    csl.version = `Version ${version}`;
  }
  if (csl.type === "book") {
    for (const key of ["genre", "medium"]) {
      const value = csl[key];
      if (typeof value === "string" && EBOOK_FORMAT_REGEX.test(value.trim())) {
        Reflect.deleteProperty(csl, key);
      }
    }
  }
}

/**
 * MLA 9 data rules the CSL style cannot express:
 * - a speech or lecture is dated by its event (the style reads only `event-date` for it),
 *   so the entered date stands in when no event date is given;
 * - an online video in a container is dated by its upload day, not a film's release year
 *   (style.mla.org: "YouTube, uploaded by …, 17 July 2014, URL"); as a web item it gets the
 *   style's full-date branch;
 * - a free-text description such as "Video" or "Photograph" is MLA's optional type-of-work
 *   element, placed at the end of the entry ("… URL. Press release.", style.mla.org),
 *   not after the title, so it moves from `genre` to `medium`, which the style prints last.
 */
function adaptForMla(csl: Record<string, unknown>): void {
  const type = csl.type;
  if (
    (type === "motion_picture" || type === "graphic" || type === "speech") &&
    csl.genre &&
    !csl.medium
  ) {
    csl.medium = csl.genre;
    Reflect.deleteProperty(csl, "genre");
  }
  if (type === "speech" && csl.issued && !csl["event-date"]) {
    csl["event-date"] = csl.issued;
  }
  if (type === "motion_picture" && csl["container-title"] && csl.URL) {
    csl.type = "webpage";
  }
}

/**
 * Apply the style-specific data rules above to one CSL record, returning a copy (or the
 * record itself when nothing applies). The record a caller stores is never changed.
 */
function adaptCslForStyle(
  record: Record<string, unknown>,
  template: string
): Record<string, unknown> {
  const csl = { ...record };
  protectDomainNames(csl);
  if (template === APA_STYLE_SLUG) {
    adaptForApa(csl);
  } else if (MLA_STYLE_SLUG_REGEX.test(template)) {
    adaptForMla(csl);
  }
  return csl;
}

/** Normalize citation.js format result to a single string (handles array, object, or string). */
function normalizeBibliographyOutput(raw: unknown): string {
  if (typeof raw === "string") {
    return raw;
  }
  if (Array.isArray(raw)) {
    return raw.map(String).join("\n\n");
  }
  if (raw != null && typeof raw === "object") {
    const values = Object.values(raw).filter(
      (v): v is string => typeof v === "string"
    );
    if (values.length > 0) {
      return values.join("\n\n");
    }
  }
  return String(raw ?? "");
}

/**
 * Citation render mode (issue #93). `"bibliography"` renders the reference-list entry
 * (the historical, default behavior). `"note"` renders the full footnote/endnote via
 * citeproc's `<citation>` layout — the form note-based styles (Chicago notes-and-
 * bibliography) are *defined by*. Defaulting to `"bibliography"` keeps every existing
 * caller byte-identical.
 */
export type CitationMode = "bibliography" | "note";

/**
 * CSL locator labels offered for in-text citations, e.g. `(Okafor, 2019, p. 88)`.
 *
 * These are CSL 1.0.1 `locator` terms, so citeproc renders each one the way the chosen
 * style abbreviates it — APA gives "p. 88" and "para. 7", and a style with its own
 * term for a locator uses that instead. The list is an allow-list on purpose: the value
 * reaches citeproc from a query string, and an unrecognised label makes it emit a raw
 * unlocalized term rather than failing loudly.
 */
export const CITATION_LOCATOR_LABELS = [
  "page",
  "chapter",
  "section",
  "paragraph",
  "line",
  "verse",
  "figure",
  "table",
  "note",
  "part",
  "volume",
  "issue",
  "book",
  "column",
  "folio",
  "opus",
  "sub verbo",
] as const;

export type CitationLocatorLabel = (typeof CITATION_LOCATOR_LABELS)[number];

export interface CitationLocator {
  /** Which kind of locator this is. Must be one of {@link CITATION_LOCATOR_LABELS}. */
  label: CitationLocatorLabel;
  /** The locator itself: "88", "12–14", "4", "2.1". */
  value: string;
}

/** One source in a composite citeproc citation cluster. */
export interface CitationClusterItem {
  entry: EditableEntry;
  /** Optional source-specific pin cite. */
  locator?: CitationLocator | null;
}

// A pin cite typed with its section or paragraph sign: "§ 5", "§§ 5–7", "¶ 12".
const LOCATOR_SIGN_REGEX = /^(§§?|¶¶?)\s*(.+)$/u;

/** Longest accepted locator value — a pin cite, not a paragraph of text. */
const MAX_LOCATOR_LENGTH = 40;

/**
 * Fixed CSL id used only when rendering a citation with a locator, so the citation
 * item can name the entry it applies to. Every other path leaves the id to
 * citation-js, keeping existing output unchanged.
 */
const LOCATOR_CITE_ID = "citationeasy-locator-entry";
const CLUSTER_CITE_ID_PREFIX = "citationeasy-cluster-entry-";

export function isValidLocatorLabel(
  value: unknown
): value is CitationLocatorLabel {
  return (
    typeof value === "string" &&
    CITATION_LOCATOR_LABELS.includes(value as CitationLocatorLabel)
  );
}

/**
 * Validate a caller-supplied locator, or return null.
 *
 * Returns null rather than throwing so a bad locator degrades to a plain in-text
 * citation instead of costing the user the citation entirely.
 */
export function normalizeLocator(input: unknown): CitationLocator | null {
  if (!input || typeof input !== "object") {
    return null;
  }
  const { label, value } = input as { label?: unknown; value?: unknown };
  if (!(isValidLocatorLabel(label) && typeof value === "string")) {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_LOCATOR_LENGTH) {
    return null;
  }
  // A value that carries its own sign — "§ 5", "¶ 12" — names its locator; the style
  // prints the label itself, so the sign is dropped rather than printed after "p.".
  const signed = LOCATOR_SIGN_REGEX.exec(trimmed);
  if (signed?.[2]) {
    return {
      label: signed[1].startsWith("§") ? "section" : "paragraph",
      value: signed[2],
    };
  }
  return { label, value: trimmed };
}

// Maps the render mode to citeproc's two layout entry points: the reference-list
// ("bibliography") layout vs. the in-text / note cluster ("citation") layout.
const MODE_TO_CSL_FORMAT: Record<CitationMode, "bibliography" | "citation"> = {
  bibliography: "bibliography",
  note: "citation",
};

/**
 * Last-resort HTML→text path for long-tail styles whose text layout throws citeproc's
 * "not iterable" error. Shared by both render modes — mirrors the previous inline
 * fallback (strip tags, collapse whitespace) exactly so default output is unchanged.
 */
function formatViaHtmlFallback(
  data: Cite,
  cslFormat: "bibliography" | "citation",
  template: string,
  locale: string
): string {
  try {
    const raw = data.format(cslFormat, {
      format: "html",
      template,
      lang: locale,
    });
    const html = normalizeBibliographyOutput(raw);
    const text = html
      .replace(HTML_TAGS_REGEX, " ")
      .replace(WHITESPACE_REGEX, " ")
      .trim();
    return text || "Citation generated (style may have limited support).";
  } catch {
    throw new Error(
      "This citation style produced an unexpected result. Try another style."
    );
  }
}

/**
 * `"text"` is the plain reference shown on the page. `"html"` is citeproc's HTML for the
 * same render, with the style's italics and other markup, for a rich clipboard copy.
 */
export type CitationOutputFormat = "text" | "html";

/** Format a citation from an editable entry (no fetch). */
export async function formatFromEntry(
  entry: EditableEntry,
  style: string,
  cslPath?: string,
  lang?: string,
  mode: CitationMode = "bibliography",
  locator?: CitationLocator | null,
  outputFormat: CitationOutputFormat = "text"
): Promise<{ citation: string }> {
  if (!isAllowedStyle(style)) {
    throw new Error("Invalid style slug.");
  }
  // Validated against the offline-locale allow-list; unknown/undefined → "en-US"
  // (the byte-identical default path) with no network fetch. Non-bundled locales are
  // vendored under src/data/csl-locales and registered here on first use; en-US and the
  // other four bundled locales return from this immediately.
  const locale = normalizeOutputLocale(lang);
  await ensureLocaleRegistered(locale);
  await ensureStyleRegistered(style.trim(), cslPath);
  const cslFormat = MODE_TO_CSL_FORMAT[mode];
  const template = style.trim();
  const csl = adaptCslForStyle(editableEntryToCsl(entry), template);

  // A locator is a property of the *citation*, not of the source, so it rides on the
  // citation item rather than the CSL entry — that is what turns "(Okafor, 2019)" into
  // "(Okafor, 2019, p. 88)". Naming the item requires a known id, and citation-js
  // otherwise assigns a random one, so a fixed id is set ONLY on this path: every
  // existing call keeps the generated id and byte-identical output. A locator means
  // nothing to the reference-list layout, so it is ignored there.
  const withLocator = Boolean(locator) && cslFormat === "citation";
  if (withLocator) {
    csl.id = LOCATOR_CITE_ID;
  }
  const data = new Cite([csl]);

  const citeOptions: Record<string, unknown> = {
    format: outputFormat,
    template,
    lang: locale,
  };
  if (withLocator && locator) {
    citeOptions.entry = [
      { id: LOCATOR_CITE_ID, locator: locator.value, label: locator.label },
    ];
  }
  let raw: unknown;
  try {
    raw = data.format(cslFormat, citeOptions);
  } catch (err) {
    if (
      outputFormat === "text" &&
      err instanceof Error &&
      BIBLIOGRAPHY_ERROR_REGEX.test(err.message)
    ) {
      return {
        citation: formatViaHtmlFallback(data, cslFormat, template, locale),
      };
    }
    throw err;
  }
  const output = normalizeBibliographyOutput(raw);
  return {
    citation: outputFormat === "html" ? output.trim() : output,
  };
}

/**
 * Format the IN-TEXT citation for an entry (issue #109) — citeproc's CSL `<citation>`
 * layout, which is the form distinct from the reference-list entry: `(Smith, 2020)` for
 * author-date styles, `[1]` for IEEE, `(1)` for Vancouver, and the full note for note-based
 * styles (Chicago notes & bibliography).
 *
 * This is the SAME citeproc entry point as note-mode rendering (issue #93), so it reuses
 * `formatFromEntry`'s style allow-list / locale guards, the limited-support HTML fallback,
 * and the no-network default path — making it safe across all 10,832 styles. The output is
 * engine output, so it is snapshot-locked by golden tests exactly like the bibliography
 * path. Returns the text under `inText`.
 */
export async function formatInText(
  entry: EditableEntry,
  style: string,
  cslPath?: string,
  lang?: string,
  locator?: CitationLocator | null
): Promise<{ inText: string }> {
  const { citation } = await formatFromEntry(
    entry,
    style,
    cslPath,
    lang,
    "note",
    locator
  );
  return { inText: citation };
}

/**
 * Format several sources as one citeproc citation cluster.
 *
 * The citation items are submitted together in one `data.format("citation")` call so
 * the CSL style controls cluster delimiters, numeric ranges, disambiguation, note
 * punctuation, and source-specific locators. Exact duplicate citation items are
 * suppressed, while the same source with a different locator remains a distinct item.
 */
export async function formatInTextCluster(
  items: CitationClusterItem[],
  style: string,
  cslPath?: string,
  lang?: string
): Promise<{ inText: string }> {
  if (!isAllowedStyle(style)) {
    throw new Error("Invalid style slug.");
  }
  if (items.length === 0) {
    throw new Error("A citation cluster needs at least one item.");
  }

  const locale = normalizeOutputLocale(lang);
  await ensureLocaleRegistered(locale);
  await ensureStyleRegistered(style.trim(), cslPath);

  const entries: Record<string, unknown>[] = [];
  const sourceIds = new Map<string, string>();
  const citationItems: Record<string, string>[] = [];
  const seenCitationItems = new Set<string>();

  const template = style.trim();
  for (const item of items) {
    const csl = adaptCslForStyle(editableEntryToCsl(item.entry), template);
    const sourceKey = JSON.stringify(csl);
    let id = sourceIds.get(sourceKey);
    if (!id) {
      id = `${CLUSTER_CITE_ID_PREFIX}${entries.length + 1}`;
      sourceIds.set(sourceKey, id);
      csl.id = id;
      entries.push(csl);
    }

    const locator = normalizeLocator(item.locator);
    const citationItemKey = `${id}\u0000${locator?.label ?? ""}\u0000${locator?.value ?? ""}`;
    if (seenCitationItems.has(citationItemKey)) {
      continue;
    }
    seenCitationItems.add(citationItemKey);
    citationItems.push({
      id,
      ...(locator
        ? { locator: locator.value, label: locator.label }
        : undefined),
    });
  }

  const data = new Cite(entries);
  const citeOptions = {
    format: "text",
    template,
    lang: locale,
    entry: citationItems,
  };
  let raw: unknown;
  try {
    raw = data.format("citation", citeOptions);
  } catch (err) {
    if (err instanceof Error && BIBLIOGRAPHY_ERROR_REGEX.test(err.message)) {
      return {
        inText: formatViaHtmlFallback(data, "citation", template, locale),
      };
    }
    throw err;
  }
  return { inText: normalizeBibliographyOutput(raw) };
}

/** One source a document cites, keyed by the caller's own stable id. */
export interface DocumentSource {
  id: string;
  entry: EditableEntry;
}

/** One in-text citation in a document, in reading order. */
export interface DocumentCitation {
  items: { id: string; locator?: CitationLocator | null }[];
  /**
   * Footnote number for note styles, so citeproc can choose full, short, and "Ibid."
   * forms. Omit (or 0) for an in-text citation.
   */
  noteIndex?: number;
}

/** citeproc's layout hints for the reference list, from `makeBibliography()`. */
export interface DocumentBibliographyFormat {
  hangingIndent: boolean;
  /** "flush" / "margin" when the style puts a label ("[1]") before a tab; else null. */
  secondFieldAlign: "flush" | "margin" | null;
  lineSpacing: number;
  entrySpacing: number;
}

export interface FormattedDocument {
  /** CSL class: "in-text" (author-date, numeric, label) or "note". */
  styleClass: "in-text" | "note";
  /** citeproc HTML for each citation, in the order supplied. */
  citations: string[];
  /** citeproc HTML for each reference-list entry, in the style's sort order. */
  bibliography: { id: string; html: string }[];
  bibliographyFormat: DocumentBibliographyFormat;
}

interface CslProcessor {
  opt: { class?: string };
  rebuildProcessorState(
    citations: Record<string, unknown>[],
    mode: string,
    uncited: string[]
  ): [string, number, string][];
  makeBibliography():
    | false
    | [
        {
          entry_ids: string[][];
          hangingindent?: number | boolean;
          "second-field-align"?: string | false;
          linespacing?: number;
          entryspacing?: number;
        },
        string[],
      ];
}

const DOCUMENT_CITE_ID_PREFIX = "citationeasy-document-entry-";
const CSL_ENTRY_DIV_REGEX = /^\s*<div class="csl-entry">([\s\S]*)<\/div>\s*$/;

/**
 * Format every citation in a document, plus its reference list, in one citeproc run.
 *
 * Rendering each citation on its own cannot be right for a whole manuscript: numeric
 * styles number sources in order of first citation, author-date styles disambiguate
 * "Smith 2020a" from "Smith 2020b" across the reference list, and note styles shorten
 * a repeat citation or print "Ibid.". All of that is processor state, so the clusters
 * go to citeproc together, in reading order, through `rebuildProcessorState`, and the
 * bibliography comes from the same engine. Output is citeproc HTML so italics and
 * small caps survive; callers convert it for their surface.
 */
export async function formatDocumentCitations(
  sources: DocumentSource[],
  citations: DocumentCitation[],
  style: string,
  cslPath?: string,
  lang?: string
): Promise<FormattedDocument> {
  if (!isAllowedStyle(style)) {
    throw new Error("Invalid style slug.");
  }
  if (citations.length === 0) {
    throw new Error("A document needs at least one citation.");
  }
  const locale = normalizeOutputLocale(lang);
  await ensureLocaleRegistered(locale);
  await ensureStyleRegistered(style.trim(), cslPath);
  const template = style.trim();

  // Internal ids keep caller-chosen ids from colliding with anything citeproc uses.
  const internalIds = new Map<string, string>();
  const callerIds = new Map<string, string>();
  const data: Record<string, unknown>[] = [];
  for (const source of sources) {
    if (internalIds.has(source.id)) {
      throw new Error(`Duplicate source id "${source.id}".`);
    }
    const internal = `${DOCUMENT_CITE_ID_PREFIX}${data.length + 1}`;
    internalIds.set(source.id, internal);
    callerIds.set(internal, source.id);
    const csl = adaptCslForStyle(editableEntryToCsl(source.entry), template);
    csl.id = internal;
    data.push(csl);
  }

  const clusters = citations.map((citation, index) => ({
    citationID: `citationeasy-document-citation-${index + 1}`,
    citationItems: citation.items.map((item) => {
      const id = internalIds.get(item.id);
      if (!id) {
        throw new Error(
          `Citation ${index + 1} cites unknown source "${item.id}".`
        );
      }
      const locator = normalizeLocator(item.locator);
      return locator
        ? { id, locator: locator.value, label: locator.label }
        : { id };
    }),
    properties: { noteIndex: citation.noteIndex ?? 0 },
  }));

  const createEngine = plugins.config.get("@csl")?.engine;
  if (!createEngine) {
    throw new Error("The citation engine is not available.");
  }
  // Both calls are synchronous, so nothing else can touch the cached engine between them.
  const engine = createEngine(
    util.downgradeCsl(data),
    template,
    locale,
    "html"
  ) as CslProcessor;
  let rendered: [string, number, string][];
  let bibliography: ReturnType<CslProcessor["makeBibliography"]>;
  try {
    rendered = engine.rebuildProcessorState(clusters, "html", []);
    bibliography = engine.makeBibliography();
  } finally {
    // The engine is cached per style and locale; leave no document state behind for
    // the next caller, even when rendering threw.
    engine.rebuildProcessorState([], "html", []);
  }
  const byCitationId = new Map(rendered.map(([id, , html]) => [id, html]));

  const format: DocumentBibliographyFormat = {
    hangingIndent: false,
    secondFieldAlign: null,
    lineSpacing: 1,
    entrySpacing: 1,
  };
  const entries: { id: string; html: string }[] = [];
  if (bibliography) {
    const [meta, bodies] = bibliography;
    format.hangingIndent = Boolean(meta.hangingindent);
    const align = meta["second-field-align"];
    format.secondFieldAlign =
      align === "flush" || align === "margin" ? align : null;
    format.lineSpacing = meta.linespacing ?? 1;
    format.entrySpacing = meta.entryspacing ?? 1;
    bodies.forEach((body, index) => {
      const internal = meta.entry_ids[index]?.[0] ?? "";
      entries.push({
        id: callerIds.get(internal) ?? internal,
        html: (CSL_ENTRY_DIV_REGEX.exec(body)?.[1] ?? body).trim(),
      });
    });
  }

  return {
    styleClass: engine.opt.class === "note" ? "note" : "in-text",
    citations: clusters.map(
      (cluster) => byCitationId.get(cluster.citationID) ?? ""
    ),
    bibliography: entries,
    bibliographyFormat: format,
  };
}

/** Run a Cite.async lookup with a hard timeout so a slow remote source can't hang the UI. */
function citeAsyncWithTimeout(
  lookup: string,
  timeoutMessage: string,
  opts?: { forceType: PmidLookup["forceType"] }
): Promise<Cite> {
  return Promise.race([
    Cite.async(lookup, opts),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(timeoutMessage)), CITE_ASYNC_TIMEOUT_MS)
    ),
  ]);
}

/** Resolve a YouTube URL via oEmbed, falling back to the generic scraper if oEmbed fails. */
async function resolveYouTubeCsl(
  url: string
): Promise<Record<string, unknown>> {
  try {
    return await youtubeToCsl(url);
  } catch {
    return await urlToCsl(url);
  }
}

/**
 * Dispatch a single auto-detect input to the right resolver, in priority order. Identifier
 * shapes (ISBN, PMID, arXiv, YouTube) are matched BEFORE the generic website/DOI branches so a
 * pasted source URL hits its specialized exporter instead of the generic HTML scraper.
 */
async function resolveInputToCite(trimmed: string): Promise<Cite> {
  if (looksLikeIsbn(trimmed)) {
    return new Cite([await isbnToCsl(normalizeIsbn(trimmed))]);
  }
  // PubMed ids (incl. pubmed.ncbi.nlm.nih.gov URLs) → the PubMed exporter, not the scraper.
  const pmid = parsePmid(trimmed);
  if (pmid) {
    return await citeAsyncWithTimeout(
      pmid.id,
      "PubMed lookup timed out. Please try again.",
      { forceType: pmid.forceType }
    );
  }
  // arXiv ids resolve through their deterministic DataCite DOI.
  const arxivId = parseArxivId(trimmed);
  if (arxivId) {
    return await citeAsyncWithTimeout(
      arxivToDoi(arxivId),
      "arXiv lookup timed out. Please try again."
    );
  }
  // YouTube video URLs resolve via the keyless oEmbed endpoint so a pasted link produces a
  // correct [Video] citation (channel as author) instead of a plain `webpage`.
  if (parseYouTubeId(trimmed)) {
    return new Cite([await resolveYouTubeCsl(trimmed)]);
  }
  if (isWebsiteUrl(trimmed)) {
    return new Cite([await urlToCsl(trimmed)]);
  }
  if (isDoi(trimmed)) {
    return await citeAsyncWithTimeout(
      trimmed,
      "DOI lookup timed out. Please try again."
    );
  }
  return new Cite(trimmed);
}

export async function formatCitation(
  input: string,
  style: string,
  cslPath?: string,
  lang?: string,
  options: { html?: boolean } = {}
): Promise<{
  citation: string;
  entry: EditableEntry | null;
  /** The same reference as HTML, when `options.html` is set and the style renders it. */
  html?: string;
}> {
  if (!input?.trim()) {
    throw new Error("Input is required.");
  }
  if (input.length > MAX_INPUT_LENGTH) {
    throw new Error(`Input too long (max ${MAX_INPUT_LENGTH} characters).`);
  }
  if (!isAllowedStyle(style)) {
    throw new Error("Invalid style slug.");
  }
  // See formatFromEntry: validated locale, defaults to the unchanged "en-US" path.
  const locale = normalizeOutputLocale(lang);
  await ensureLocaleRegistered(locale);
  await ensureStyleRegistered(style.trim(), cslPath);

  const resolved = await resolveInputToCite(input.trim());
  const resolvedRecords =
    (resolved as unknown as { data?: Record<string, unknown>[] }).data ?? [];
  const data = new Cite(
    resolvedRecords.map((record) => adaptCslForStyle(record, style.trim()))
  );

  let raw: unknown;
  try {
    raw = data.format("bibliography", {
      format: "text",
      template: style.trim(),
      lang: locale,
    });
  } catch (err) {
    if (err instanceof Error && BIBLIOGRAPHY_ERROR_REGEX.test(err.message)) {
      try {
        raw = data.format("bibliography", {
          format: "html",
          template: style.trim(),
          lang: locale,
        });
        const html = normalizeBibliographyOutput(raw);
        const text = html
          .replace(HTML_TAGS_REGEX, " ")
          .replace(WHITESPACE_REGEX, " ")
          .trim();
        raw = text || "Citation generated (style may have limited support).";
      } catch {
        throw new Error(
          "This citation style produced an unexpected result. Try another style."
        );
      }
    } else {
      throw err;
    }
  }
  const citationStr = normalizeBibliographyOutput(raw);

  const first = resolvedRecords[0];
  const entry =
    first && typeof first === "object"
      ? cslToEditableEntry(first as Record<string, unknown>)
      : null;

  if (!options.html) {
    return { citation: citationStr, entry };
  }
  return {
    citation: citationStr,
    entry,
    html: renderHtmlOrNothing(data, style.trim(), locale),
  };
}

/** The bibliography HTML for a resolved record, or nothing when the style cannot render it. */
function renderHtmlOrNothing(
  data: Cite,
  template: string,
  locale: string
): string | undefined {
  try {
    const html = normalizeBibliographyOutput(
      data.format("bibliography", { format: "html", template, lang: locale })
    ).trim();
    return html || undefined;
  } catch {
    return undefined;
  }
}

export type BibliographyImportFormat = "bibtex" | "ris" | "csljson";

const IMPORT_FORMAT_LABELS: Record<BibliographyImportFormat, string> = {
  bibtex: "BibTeX",
  ris: "RIS",
  csljson: "CSL-JSON",
};

export interface BibliographyImportResult {
  /** Parsed entries, capped at MAX_IMPORT_ENTRIES. */
  entries: EditableEntry[];
  /** Total records found in the input (before the cap). */
  total: number;
  /** Records dropped because the input exceeded MAX_IMPORT_ENTRIES. */
  skipped: number;
  /** Format the input was treated as (drives the import analytics signal). */
  format: BibliographyImportFormat;
}

/** Detect bulk-import format from content; falls back to BibTeX when ambiguous. */
function detectImportFormat(text: string): BibliographyImportFormat {
  // CSL-JSON is the only format whose payload starts with a JSON array/object — sniff it
  // first so a `references.json` (Pandoc/Zotero) isn't misread as BibTeX.
  const head = text.trimStart()[0];
  if (head === "[" || head === "{") {
    return "csljson";
  }
  return RIS_FORMAT_REGEX.test(text) ? "ris" : "bibtex";
}

/**
 * Parse CSL-JSON (Pandoc/Quarto `references.json`, Zotero "CSL JSON" export) into raw CSL
 * records. A direct `JSON.parse` (rather than `new Cite()`) gives a clear error on bad JSON
 * and avoids the BibTeX/RIS sniff. Accepts either a top-level array (the canonical shape) or
 * a single object, then drops any non-object members.
 */
function parseCslJsonRecords(
  text: string,
  label: string
): Record<string, unknown>[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error(
      `Could not parse ${label}. Make sure the file contains a valid CSL-JSON array.`
    );
  }
  const items = Array.isArray(parsed) ? parsed : [parsed];
  return items.filter(
    (item): item is Record<string, unknown> =>
      typeof item === "object" && item !== null && !Array.isArray(item)
  );
}

/** Parse BibTeX/RIS via the already-loaded Citation.js plugins into raw CSL records. */
function parseWithCite(text: string, label: string): Record<string, unknown>[] {
  let records: unknown;
  try {
    records = (new Cite(text) as unknown as { data?: unknown }).data;
  } catch {
    throw new Error(
      `Could not parse ${label}. Make sure the file contains valid records.`
    );
  }
  return Array.isArray(records) ? (records as Record<string, unknown>[]) : [];
}

/**
 * Parse a bibliography library (paste or file contents) into EditableEntry records.
 *
 * Accepts BibTeX, RIS, and CSL-JSON — the formats academic databases and Markdown-based
 * pipelines (Pandoc/Quarto/Zotero) export. Pure + offline: BibTeX/RIS reuse the already-loaded
 * `@citation-js/plugin-bibtex` / `@citation-js/plugin-ris`; CSL-JSON is parsed directly. Every
 * record then maps through `cslToEditableEntry` — the same path a single-citation lookup takes
 * — so an imported entry is indistinguishable from a hand-entered one. The `format` arg (or
 * content sniff) labels errors and drives the analytics signal. No network.
 */
export function parseBibliographyImport(
  text: string,
  format?: BibliographyImportFormat
): BibliographyImportResult {
  const resolvedFormat = format ?? detectImportFormat(text ?? "");
  const label = IMPORT_FORMAT_LABELS[resolvedFormat];

  if (!text?.trim()) {
    throw new Error(`No ${label} content to import.`);
  }
  if (text.length > MAX_IMPORT_LENGTH) {
    throw new Error(
      `Import too large (max ${MAX_IMPORT_LENGTH.toLocaleString()} characters).`
    );
  }

  const records =
    resolvedFormat === "csljson"
      ? parseCslJsonRecords(text.trim(), label)
      : parseWithCite(text.trim(), label);

  if (records.length === 0) {
    throw new Error(
      `No citations found. Paste or upload a valid ${label} file.`
    );
  }

  const entries = records
    .slice(0, MAX_IMPORT_ENTRIES)
    .map((csl) => cslToEditableEntry(csl));

  return {
    entries,
    total: records.length,
    skipped: Math.max(0, records.length - MAX_IMPORT_ENTRIES),
    format: resolvedFormat,
  };
}
